const numberTiers = {
    'pt-br': [
        {
            value: 1e3,
            symbol: 'mil'
        },
        {
            value: 1e6,
            symbol: 'mi'
        },
        {
            value: 1e9,
            symbol: 'bi'
        },
        {
            value: 1e12,
            symbol: 'tri'
        },
        {
            value: 1e15,
            symbol: 'qua'
        },
        {
            value: 1e18,
            symbol: 'qui'
        }
    ]
}

const currencies = {
    'pt-br': 'R$'
}

/**
 * Get number's length without any string manipulation
 */
const numberLength = (n) => {
    if (n < 1) {
        return 0
    }
    return Math.floor(Math.log10(n) + 1)
}

/**
 * Remove trailing zeroes from Number without string manipulation
 */
const removeNumberTrailingZeroes = (n) => {
    if (n < 1) {
        return 0
    } else if (n % 10 === 0) {
        return removeNumberTrailingZeroes(n / 10)
    } else {
        return n
    }
}

/**
 * Humanizes a number.
 * Examples:
 *
 * -1 => null
 * 0 => null
 * 1 => R$ 1
 * 1,15 => R$ 1
 * 10 => R$ 10
 * 12 => R$ 12
 * 12,15 => R$ 12
 * 100 => R$ 100
 * 123 => R$ 123
 * 123,15 => R$ 123
 * 1.000 => R$ 1 mil
 * 1.234 => R$ 1.23 mil
 * 1.234,15 => R$ 1.23 mil
 * 10.000 => R$ 10 mil
 * 12.345 => R$ 12.3 mil
 * 12.345,15 => R$ 12.3 mil
 * 100.000 => R$ 100 mil
 * 123.456 => R$ 123 mil
 * 123.456,15 => R$ 123 mil
 * 1.000.000 => R$ 1 mi
 * 1.234.567 => R$ 1.23 mi
 * 1.234.567,15 => R$ 1.23 mi
 * 10.000.000 => R$ 10 mi
 * 12.345.678 => R$ 12.3 mi
 * 12.345.678,15 => R$ 12.3 mi
 * 100.000.000 => R$ 100 mi
 * 123.456.789 => R$ 123 mi
 * 123.456.789,15 => R$ 123 mi
 * 1.000.000.000 => R$ 1 bi
 * 1.234.567.890 => R$ 1.23 bi
 * 1.234.567.890,15 => R$ 1.23 bi
 * 10.000.000.000 => R$ 10 bi
 * 12.345.678.900 => R$ 12.3 bi
 * 12.345.678.900,15 => R$ 12.3 bi
 * 100.000.000.000 => R$ 100 bi
 * 123.456.789.000 => R$ 123 bi
 * 120.456.789.000 => R$ 120 bi
 * 100.456.789.000 => R$ 100 bi
 * 123.456.789.000,15 => R$ 123 bi
 * 1.000.000.000.000 => R$ 1 tri
 * 1.234.567.890.000 => R$ 1.23 tri
 * 1.204.567.890.000 => R$ 1.2 tri
 * 1.004.567.890.000 => R$ 1 tri
 * 1.234.567.890.000,15 => R$ 1.23 tri
 * 22.403.548,15 => R$ 22.4 mi
 */
const humanizeNumber = (_value, locale = 'pt-br') => {
    let value = _value || 0

    const tiers = numberTiers[locale]

    if (typeof value === 'string') {
        value = parseFloat(value)
        if (isNaN(value)) {
            return null
        }
    }

    if (value < 1) {
        return null
    }

    // value = 22403548.15
    // suffixed = 22403548
    let suffixed = Math.floor(value)

    // tier = { value: 1000000, symbol: 'mi' }
    const [tier] = tiers
        .filter((n) => value >= n.value)
        .slice(-1)

    if (tier) {
        // cleanValue = 22403548
        const cleanValue = Math.floor(value)

        // take left number dividing it by its tier
        // so, having 22403548, tier.value = 1000000, floor(22403548/1000000) = 2
        const left = Math.floor(cleanValue / tier.value)

        // take left number length without any string manipulation
        // leftLength = 2, which will be 22
        const leftLength = numberLength(left)

        // take right number length then subtract left length from it
        // so, having 22403548, length = 8, leftLength = 2, then rightLength = 6
        const rightLength = numberLength(cleanValue) - leftLength

        // remove right numbers so it will be readable
        // the base is 3 numbers at maximum
        // so, having leftLength = 2, then rightGap = 1
        const rightGap = Math.max(3 - leftLength, 0)

        // get right number's base
        // (it's highest thousand multiplier, 4321 = 1000, 50000 = 10000, etc.)
        // so, having rightGap = 1, rightLength = 6,
        // then rightGapBase = 10 ^ (6 - 1)  = 100000
        const rightGapBase = Math.pow(10, rightLength - rightGap)

        // get right number's actual value
        // so, having cleanValue = 22403548, rightLength = 6
        // then rightValue = 22403548 % (10 ^ 6) = 22403548 % 1000000 = 403548
        const rightValue = cleanValue % Math.pow(10, rightLength)

        // check if there's some padding zero, for numbers like 1.03 mi
        const paddingZeroes = rightLength - numberLength(rightValue);

        // get right number's displayable value dividing by it's base
        // so, having rightValue = 403548, rightGapBase = 100000
        // then right = floor(403548 / 100000) = 4
        const right = Math.floor(rightValue / rightGapBase)

        const fixedRight = removeNumberTrailingZeroes(right)

        if (fixedRight > 0 && paddingZeroes > 0) {
            // suffixed = 22.4 mi
            return `${left}.${Array(paddingZeroes).fill("0")}${fixedRight} ${tier.symbol}`
        } else if (fixedRight > 0) {
            // suffixed = 22.4 mi
            return `${left}.${fixedRight} ${tier.symbol}`
        } else {
            return `${left} ${tier.symbol}`
        }
    }

    return suffixed
}

const humanizeMoney = (_value, locale = 'pt-br') => {
    let value = _value || 0
    const currency = currencies[locale]

    if (typeof value === 'string' && value.indexOf(currency) > -1) {
        return value
    }

    const number = humanizeNumber(value, locale)

    if (number === null) {
        return null
    }

    return `${currency} ${number}`
}

module.exports = {
    humanizeMoney,
    humanizeNumber
}


// WEBPACK FOOTER //
// ./resources/assets/javascript/microsistec/humanize.js